// ============================================================================
// G-005 — Actor Health Monitoring
// ============================================================================
// Track per-actor success/failure events untuk detect when "radar" failing.
//
// Storage:
//   - DB: osint_audit table — actions 'actor_success' / 'actor_failure'
//   - In-memory cache: per-platform status (TTL 60s) untuk performance
//
// Health derivation (from rolling 24h window):
//   - HEALTHY:  success_rate >= 0.7  AND last_success < 1h ago
//   - DEGRADED: success_rate 0.3-0.7 OR last_success 1-6h ago
//   - FAILING:  success_rate < 0.3   OR consecutive_failures >= 3
//   - UNKNOWN:  no recent data (cold start)
//
// Operator-side: when FAILING, show "[Platform] tidak tersedia" message
// Admin-side: dashboard di System Log page (future)
// ============================================================================

import { supabase } from '../supabase';

const CACHE_TTL_MS  = 60000;      // 60 seconds in-memory cache
// Sharpening 2026-05-19: reduce 24h → 6h supaya failure historical roll off
// lebih cepat. Kasus TikTok: 15 failure kemarin + 10 success hari ini = 40%
// rate → DEGRADED. Dgn 6h window, cuma recent runs dihitung → HEALTHY lebih cepat.
const ROLLING_HOURS = 6;
const FAILURE_THRESHOLD = 3;       // 3+ consecutive fail = FAILING

const _cache = new Map(); // platform → { status, ts, details }

/**
 * Record actor success — fire-and-forget audit log
 */
export async function recordActorSuccess(platform, actor, durationMs) {
  invalidateCache(platform);
  try {
    await supabase.from('osint_audit').insert({
      action: 'actor_success',
      operator_id: 'system',
      subject_id: 'system', // bukan null — osint_audit.subject_id NOT NULL
      metadata: { platform, actor, duration_ms: durationMs, ts: Date.now() },
    });
  } catch { /* fire-and-forget */ }
}

/**
 * Record actor failure — fire-and-forget audit log
 */
export async function recordActorFailure(platform, actor, errorMessage, durationMs) {
  invalidateCache(platform);
  try {
    await supabase.from('osint_audit').insert({
      action: 'actor_failure',
      operator_id: 'system',
      subject_id: 'system', // bukan null — osint_audit.subject_id NOT NULL
      metadata: { platform, actor, error: String(errorMessage).slice(0, 200), duration_ms: durationMs, ts: Date.now() },
    });
  } catch { /* fire-and-forget */ }
}

function invalidateCache(platform) { _cache.delete(platform); }

/**
 * Derive platform health status dari osint_audit log (rolling 24h window).
 * Returns { status, success_rate, last_success_ts, last_failure_ts, consecutive_failures, total }
 */
export async function getPlatformHealth(platform) {
  const cached = _cache.get(platform);
  if (cached && (Date.now() - cached.ts) < CACHE_TTL_MS) return cached.details;

  const cutoff = new Date(Date.now() - ROLLING_HOURS * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from('osint_audit')
    .select('action, metadata, created_at')
    .in('action', ['actor_success', 'actor_failure'])
    .gte('created_at', cutoff)
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) {
    console.warn('[actorHealth] query fail:', error.message);
    return { status: 'UNKNOWN', success_rate: 0, total: 0 };
  }

  // Filter by platform di metadata. Plus: 'zero_results' adalah hasil VALID
  // (subject tidak punya akun di platform tsb), BUKAN actor failure. Re-categorize.
  const events = (data || [])
    .filter(e => e.metadata?.platform === platform)
    .map(e => (e.action === 'actor_failure' && e.metadata?.error === 'zero_results')
      ? { ...e, action: 'actor_success' }  // empty result = actor working, just no match
      : e
    );
  if (events.length === 0) {
    const result = { status: 'UNKNOWN', success_rate: 0, total: 0, last_success_ts: null, last_failure_ts: null, consecutive_failures: 0 };
    _cache.set(platform, { ts: Date.now(), details: result });
    return result;
  }

  const successes = events.filter(e => e.action === 'actor_success');
  const failures  = events.filter(e => e.action === 'actor_failure');
  const successRate = successes.length / events.length;
  const lastSuccess = successes[0]?.created_at || null;
  const lastFailure = failures[0]?.created_at || null;

  // Count consecutive failures from latest event backwards
  let consecutiveFails = 0;
  for (const e of events) {
    if (e.action === 'actor_failure') consecutiveFails++;
    else break;
  }

  // Derive status
  let status = 'UNKNOWN';
  const hourAgo = Date.now() - 3600 * 1000;
  const sixHoursAgo = Date.now() - 6 * 3600 * 1000;
  const lastSuccessTime = lastSuccess ? new Date(lastSuccess).getTime() : 0;

  if (consecutiveFails >= FAILURE_THRESHOLD || successRate < 0.3) {
    status = 'FAILING';
  } else if (successRate >= 0.7 && lastSuccessTime > hourAgo) {
    status = 'HEALTHY';
  } else if (successRate >= 0.3 || lastSuccessTime > sixHoursAgo) {
    status = 'DEGRADED';
  }

  const result = {
    status,
    success_rate: Number(successRate.toFixed(2)),
    total: events.length,
    last_success_ts: lastSuccess,
    last_failure_ts: lastFailure,
    consecutive_failures: consecutiveFails,
  };
  _cache.set(platform, { ts: Date.now(), details: result });
  return result;
}

/**
 * Bulk fetch health status for multiple platforms — single DB query.
 * Returns Map<platform, healthDetails>
 */
export async function getAllPlatformsHealth(platforms) {
  const cutoff = new Date(Date.now() - ROLLING_HOURS * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from('osint_audit')
    .select('action, metadata, created_at')
    .in('action', ['actor_success', 'actor_failure'])
    .gte('created_at', cutoff)
    .order('created_at', { ascending: false })
    .limit(500);

  const result = new Map();
  if (error) {
    for (const p of platforms) result.set(p, { status: 'UNKNOWN', success_rate: 0, total: 0 });
    return result;
  }

  for (const platform of platforms) {
    // zero_results = valid empty (bukan actor crash) → treat as success
    const events = (data || [])
      .filter(e => e.metadata?.platform === platform)
      .map(e => (e.action === 'actor_failure' && e.metadata?.error === 'zero_results')
        ? { ...e, action: 'actor_success' }
        : e
      );
    if (events.length === 0) {
      result.set(platform, { status: 'UNKNOWN', success_rate: 0, total: 0, last_success_ts: null, last_failure_ts: null, consecutive_failures: 0 });
      continue;
    }
    const successes = events.filter(e => e.action === 'actor_success');
    const successRate = successes.length / events.length;
    const lastSuccess = successes[0]?.created_at || null;
    const lastFailure = events.filter(e => e.action === 'actor_failure')[0]?.created_at || null;
    let consecutiveFails = 0;
    for (const e of events) { if (e.action === 'actor_failure') consecutiveFails++; else break; }

    let status = 'UNKNOWN';
    const hourAgo = Date.now() - 3600 * 1000;
    const sixHoursAgo = Date.now() - 6 * 3600 * 1000;
    const lastSuccessTime = lastSuccess ? new Date(lastSuccess).getTime() : 0;
    if (consecutiveFails >= FAILURE_THRESHOLD || successRate < 0.3) status = 'FAILING';
    else if (successRate >= 0.7 && lastSuccessTime > hourAgo) status = 'HEALTHY';
    else if (successRate >= 0.3 || lastSuccessTime > sixHoursAgo) status = 'DEGRADED';

    result.set(platform, {
      status,
      success_rate: Number(successRate.toFixed(2)),
      total: events.length,
      last_success_ts: lastSuccess,
      last_failure_ts: lastFailure,
      consecutive_failures: consecutiveFails,
    });
  }
  return result;
}

/**
 * Format status untuk UI (color + label).
 */
export function statusBadge(status, lang = 'id') {
  const map = {
    HEALTHY:  { color: '#00ff88', label_id: 'AKTIF',          label_en: 'HEALTHY' },
    DEGRADED: { color: '#ffd60a', label_id: 'TERGANGGU',      label_en: 'DEGRADED' },
    FAILING:  { color: '#ff2d55', label_id: 'GAGAL',          label_en: 'FAILING' },
    UNKNOWN:  { color: '#6a7a90', label_id: 'BELUM DIKETAHUI', label_en: 'UNKNOWN' },
  };
  const m = map[status] || map.UNKNOWN;
  return { color: m.color, label: lang === 'id' ? m.label_id : m.label_en };
}
